import type {
    ComponentSchemasDefinition,
    CoreSchemaConfig,
    SchemaService,
    SchemaConfig,
    SchemaFile,
    NamespaceConfig,
    BaseLogger,
    DSConfig
} from '@wix/document-services-types'
import _ from 'lodash'
import common from '../dist/schemas/common.json'
import {DATA_TYPES} from '../common/namespaceMapping'
import theSchemasFormerlyKnownAsCommon from '../dist/schemas/commonSchemas.json'
import {extendDataNodeSchemas} from './schemas/schemaUtils'
import {CannotFindSchemaError, SchemaValidationError, SchemaValidationErrorDetails} from './schemas/errors'
import {createSchemaCore, ValidationFunction} from './schemas/schemaCore'
import {getUnifiedSchemasConfig} from './configs/schemaConfigs'
import * as whitelistCleanup from './schemas/whitelistCleanup'
// eslint-disable-next-line import/no-unresolved
import {coreUtils} from '@wix/santa-ds-libs/basic'
// @ts-expect-error
import * as csstree from 'css-tree'

const {isValidFontFamily} = coreUtils.fontUtils.createFontUtils()

const notActuallySchemas = [
    'definition',
    'componentsDefinitionsMap',
    'allComponentsDefinitionsMap',
    'componentTypeAliases',
    'skinsByComponentType',
    'containers'
]

function isExcludedFromValidation(type: string): boolean {
    return ['AppVars'].includes(type)
}

const getSchemasFromConfig = (schemasAndDefinitionsArrangedByDataType: any) => ({
    ..._.omit(schemasAndDefinitionsArrangedByDataType, notActuallySchemas),
    theSchemasFormerlyKnownAsCommon,
    common
})

enum CssSyntax {
    Property = 'property',
    Type = 'type'
}

const extendedCsstree = csstree.fork({
    units: {
        length: [...csstree.lexer.units.length, 'spx']
    }
})

const matchSyntax = (syntax: CssSyntax, name: string, value: csstree.CssNode): csstree.LexerMatchResult => {
    switch (syntax) {
        case CssSyntax.Property:
            return extendedCsstree.lexer.matchProperty(name, value)
        case CssSyntax.Type:
            return extendedCsstree.lexer.matchType(name, value)
    }
}

const createCSSFormatValidator = (cssSyntaxName: string, syntax: CssSyntax) => (val: string) => {
    try {
        const ast = extendedCsstree.parse(val, {context: 'value'})
        const matchResult = matchSyntax(syntax, cssSyntaxName, ast)
        return matchResult.error === null
    } catch {
        return false
    }
}

const customFormats: Record<string, (value: string) => boolean> = {
    'css-color': createCSSFormatValidator('color', CssSyntax.Type),
    'css-blend-mode': createCSSFormatValidator('blend-mode', CssSyntax.Type),
    'css-angle': createCSSFormatValidator('angle', CssSyntax.Type),
    'css-length-percentage': createCSSFormatValidator('length-percentage', CssSyntax.Type),
    'css-length': createCSSFormatValidator('length', CssSyntax.Type),
    'css-number': createCSSFormatValidator('number', CssSyntax.Type),
    'css-percentage': createCSSFormatValidator('percentage', CssSyntax.Type),
    'css-string': createCSSFormatValidator('string', CssSyntax.Type),
    'css-time': createCSSFormatValidator('time', CssSyntax.Type),

    'css-border-top-style': createCSSFormatValidator('border-top-style', CssSyntax.Property),
    'css-border-top-width': createCSSFormatValidator('border-top-width', CssSyntax.Property),
    'css-border-width': createCSSFormatValidator('border-width', CssSyntax.Property),
    'css-border-style': createCSSFormatValidator('border-style', CssSyntax.Property),
    'css-border-color': createCSSFormatValidator('border-color', CssSyntax.Property),
    'css-border': createCSSFormatValidator('border', CssSyntax.Property),
    'css-border-top-left-radius': createCSSFormatValidator('border-top-left-radius', CssSyntax.Property),
    'css-border-radius': createCSSFormatValidator('border-radius', CssSyntax.Property),
    'css-font': createCSSFormatValidator('font', CssSyntax.Property),
    'css-font-family': createCSSFormatValidator('font-family', CssSyntax.Property),
    'css-font-size': createCSSFormatValidator('font-size', CssSyntax.Property),
    'css-font-stretch': createCSSFormatValidator('font-stretch', CssSyntax.Property),
    'css-font-style': createCSSFormatValidator('font-style', CssSyntax.Property),
    'css-font-variant': createCSSFormatValidator('font-variant', CssSyntax.Property),
    'css-font-weight': createCSSFormatValidator('font-weight', CssSyntax.Property),
    'css-line-height': createCSSFormatValidator('line-height', CssSyntax.Property),
    'css-letter-spacing': createCSSFormatValidator('letter-spacing', CssSyntax.Property),
    'css-text-align': createCSSFormatValidator('text-align', CssSyntax.Property),
    'css-text-transform': createCSSFormatValidator('text-transform', CssSyntax.Property),
    'css-text-shadow': createCSSFormatValidator('text-shadow', CssSyntax.Property),
    'css-text-decoration': createCSSFormatValidator('text-decoration', CssSyntax.Property),
    'css-text-decoration-line': createCSSFormatValidator('text-decoration-line', CssSyntax.Property),
    'css-text-decoration-style': createCSSFormatValidator('text-decoration-style', CssSyntax.Property),
    'css-text-decoration-thickness': createCSSFormatValidator('text-decoration-thickness', CssSyntax.Property),
    'css-text-overflow': createCSSFormatValidator('text-overflow', CssSyntax.Property),
    'css-padding': createCSSFormatValidator('padding', CssSyntax.Property),
    'css-padding-top': createCSSFormatValidator('padding-top', CssSyntax.Property),
    'css-margin': createCSSFormatValidator('margin', CssSyntax.Property),
    'css-margin-top': createCSSFormatValidator('margin-top', CssSyntax.Property),
    'css-box-shadow': createCSSFormatValidator('box-shadow', CssSyntax.Property),
    'css-opacity': createCSSFormatValidator('opacity', CssSyntax.Property),
    'css-background': createCSSFormatValidator('background', CssSyntax.Property),
    'css-background-size': createCSSFormatValidator('background-size', CssSyntax.Property),
    'css-background-image': createCSSFormatValidator('background-image', CssSyntax.Property),
    'css-align-self': createCSSFormatValidator('align-self', CssSyntax.Property),
    'css-height-or-width': createCSSFormatValidator('height', CssSyntax.Property),
    'css-background-clip': createCSSFormatValidator('background-clip', CssSyntax.Property),
    'css-background-origin': createCSSFormatValidator('background-origin', CssSyntax.Property),
    'css-background-position': createCSSFormatValidator('background-position', CssSyntax.Property),
    'css-background-repeat': createCSSFormatValidator('background-repeat', CssSyntax.Property),
    'css-background-attachment': createCSSFormatValidator('background-attachment', CssSyntax.Property),
    'css-overflow': createCSSFormatValidator('overflow', CssSyntax.Property),
    'css-overflow-x': createCSSFormatValidator('overflow-x', CssSyntax.Property),
    'css-overflow-y': createCSSFormatValidator('overflow-y', CssSyntax.Property),
    'css-align-items': createCSSFormatValidator('align-items', CssSyntax.Property),
    'css-flex-direction': createCSSFormatValidator('flex-direction', CssSyntax.Property),
    'css-gap': createCSSFormatValidator('gap', CssSyntax.Property),
    'css-single-value-gap': createCSSFormatValidator('column-gap', CssSyntax.Property),
    'css-justify-content': createCSSFormatValidator('justify-content', CssSyntax.Property),
    'css-fill-with-url': createCSSFormatValidator('fill', CssSyntax.Property),
    'css-isolation': createCSSFormatValidator('isolation', CssSyntax.Property),
    'css-mix-blend-mode': createCSSFormatValidator('mix-blend-mode', CssSyntax.Property),
    'css-object-fit': createCSSFormatValidator('object-fit', CssSyntax.Property),
    'css-object-position': createCSSFormatValidator('object-position', CssSyntax.Property),
    'css-fill-opacity': createCSSFormatValidator('opacity', CssSyntax.Property), // opacity and fill-opacity have the same definition on MDN, lexer has a bug for fill-opacity
    'css-stroke': createCSSFormatValidator('stroke', CssSyntax.Property),
    'css-stroke-width': createCSSFormatValidator('stroke-width', CssSyntax.Property),
    'css-stroke-opacity': createCSSFormatValidator('stroke-opacity', CssSyntax.Property),
    'css-display': createCSSFormatValidator('display', CssSyntax.Property),
    'css-filter': createCSSFormatValidator('filter', CssSyntax.Property),
    'css-writing-mode': createCSSFormatValidator('writing-mode', CssSyntax.Property)
}

function createService(
    schemaConfig: SchemaConfig,
    schemaServiceOptions?: Record<string, boolean>,
    logger?: BaseLogger
): SchemaService {
    const coreSchemaConfig = {
        namespaces: getSchemasFromConfig(schemaConfig.schemas),
        permanentDataTypes: schemaConfig.permanentDataTypes,
        formats: {
            'font-family': isValidFontFamily,
            ...customFormats
        }
    } as CoreSchemaConfig
    if (schemaConfig.restrictedSchemas) {
        coreSchemaConfig.restrictedSchemas = getSchemasFromConfig(schemaConfig.restrictedSchemas)
    }
    if (schemaConfig.restrictionMap) {
        coreSchemaConfig.restrictionMap = getSchemasFromConfig(schemaConfig.restrictionMap)
    }
    const compDefs = {...schemaConfig.schemas.definition}
    const systemStyleIds = new Set<string>()

    _.forOwn(compDefs, definition => {
        if (definition.styles) {
            Object.keys(definition.styles).forEach(styleId => systemStyleIds.add(styleId))
        }
    })

    const aliasToCompKeyMap = new Map<string, string>()
    function getDefinition(componentType: string) {
        return compDefs[componentType] ?? compDefs[aliasToCompKeyMap.get(componentType)!]
    }

    const getComponentType = (compKey: string) => getDefinition(compKey)?.type

    const updateAliasMap = (compKey: string) => {
        const compDef = compDefs[compKey]
        if (compDef?.aliases) {
            compDef.aliases.forEach((alias: string) => {
                aliasToCompKeyMap.set(alias, compKey)
            })
        }
    }

    const updateSystemStyles = (componentDefinition: Record<string, any>) => {
        if (componentDefinition.styles) {
            Object.keys(componentDefinition.styles).forEach((styleId: string) => {
                systemStyleIds.add(styleId)
            })
        }
    }

    Object.keys(compDefs).forEach((compKey: string) => {
        updateAliasMap(compKey)
    })

    const core = createSchemaCore(coreSchemaConfig, schemaConfig.references, schemaServiceOptions, logger)

    const getSchema = (namespace: string, schemaName: string): any => {
        const cachedSchema = core.getInflatedSchemaFromCache(namespace, schemaName)
        if (cachedSchema) {
            return cachedSchema
        }
        const schema = _.get(schemaConfig.schemas, [namespace, schemaName])
        if (!schema) {
            return schema
        }
        const inflatedSchema = core.inflateSchema(namespace, schema, schemaName)
        core.addInflatedSchemaToCache(namespace, schemaName, inflatedSchema)
        return inflatedSchema
    }

    function getDefinitionByPredicate(lodashPredicate: any) {
        return _.find(compDefs, lodashPredicate)
    }

    /** Return a boolean representing whether or not the given `id` is the id of a system style
     *
     * A system style is any style that exists in a component definitions's `styles` property.
     * Note that while most components are supplied at service initialization, some
     * may be registered at runtime, which might change the results of this function on certain inputs.
     *
     * @param {string} id The id of the style
     * @returns {boolean} true if `id` is the id of a system style, false otherwise
     */
    function isSystemStyle(id: string): boolean {
        return systemStyleIds.has(id)
    }

    const createValidator =
        (coreValidator: ValidationFunction) => (dataTypeName: string, data: any, namespace: string) => {
            const designDt = DATA_TYPES.design
            const dataDt = DATA_TYPES.data

            if (isExcludedFromValidation(data?.type)) {
                return
            }

            // To preserve the legacy API, data schemas override design schemas when there's a naming conflict
            if (
                namespace === designDt &&
                core.hasSchemaForDataType(designDt, dataTypeName) &&
                core.hasSchemaForDataType(dataDt, dataTypeName)
            ) {
                coreValidator(dataDt, dataTypeName, data)
                return
            }

            const namespacesToTry = [namespace, 'mobileHints', 'theSchemasFormerlyKnownAsCommon']
            const ns = _.find(namespacesToTry, possibleNamespace =>
                core.hasSchemaForDataType(possibleNamespace, dataTypeName)
            )
            if (!ns) {
                throw new CannotFindSchemaError(namespace, dataTypeName)
            }
            coreValidator(ns, dataTypeName, data)
        }

    const validate = createValidator(core.addDefaultsAndValidate)
    const validateNoDefaults = createValidator(core.validateNoDefaults)
    const validateStrict = createValidator(core.validateStrict)

    function isValid(dataTypeName: string, data: any, namespace: string): boolean {
        try {
            validate(dataTypeName, data, namespace)
            return true
        } catch (e) {
            return false
        }
    }

    const createItemAccordingToSchema = (schemaName: string, namespace: string, overrides = {}) => {
        if (core.hasSchemaForDataType(namespace, schemaName)) {
            const item = {...overrides, type: schemaName}
            try {
                validate(schemaName, item, namespace)
            } catch (e) {
                /*
                 Do not throw on purpose when creating item according to schema.
                 It is possible we will have a schema with required fields, but no defaults - and these will be added in later
                 This is backwards compatible behavior
                 */
            }
            return item
        }
    }

    function registerComponentDefinitionAndSchemas(
        compType: string,
        {componentDefinition, dataSchemas, propertiesSchemas, otherSchemas}: ComponentSchemasDefinition,
        allowOverrides = false
    ) {
        const schemaExists = !!compDefs[compType]
        if (schemaExists && !allowOverrides) {
            return
        }
        const newCompDef = componentDefinition[compType]
        if (!['Container', 'RepeaterContainer', 'Component'].includes(newCompDef.type)) {
            newCompDef.type = 'Component'
        }
        compDefs[compType] = newCompDef
        updateAliasMap(compType)
        updateSystemStyles(newCompDef)

        core.addDataTypesToExistingNamespace(
            DATA_TYPES.data,
            extendDataNodeSchemas(dataSchemas, DATA_TYPES.data),
            schemaExists
        )
        core.addDataTypesToExistingNamespace(
            DATA_TYPES.prop,
            extendDataNodeSchemas(propertiesSchemas, DATA_TYPES.prop),
            schemaExists
        )
        const moreSchemas = otherSchemas ?? {}
        Object.keys(moreSchemas).forEach(namespace => {
            if (core.hasNamespace(namespace)) {
                core.addDataTypesToExistingNamespace(
                    namespace,
                    extendDataNodeSchemas(moreSchemas[namespace], namespace),
                    schemaExists
                )
            } else {
                throw new Error(
                    `Namespace ${namespace} does not exist. Creating new namespaces via registerComponentDefinitionAndSchemas is not supported.`
                )
            }
        })
    }

    function registerDataTypeSchema(schemasToRegister: SchemaFile, namespace: string) {
        if (core.hasNamespace(namespace)) {
            core.addDataTypesToExistingNamespace(namespace, extendDataNodeSchemas(schemasToRegister, namespace), true)
        } else {
            core.registerNamespace(namespace, schemasToRegister)
        }
    }

    function removeAdditionalProperties(namespace: string, dataTypeName: string, data: any): void {
        if (isExcludedFromValidation(data?.type)) {
            return
        }

        core.removeAdditionalProperties(namespace, dataTypeName, data)
    }

    const containerTypesSet = new Set(['Page', 'Container', 'Document', 'RepeaterContainer', 'RefComponent'])

    const isContainer = (compKey: string) => containerTypesSet.has(getComponentType(compKey))
    const isPage = (compKey: string) => getComponentType(compKey) === 'Page'
    const isRepeater = (compKey: string) => getComponentType(compKey) === 'RepeaterContainer'
    const isRefComponent = (compKey: string) => getComponentType(compKey) === 'RefComponent'

    const getNamespaceConfig = (namespace: string): NamespaceConfig => {
        return schemaConfig.namespaceConfigs[namespace]
    }

    return {
        getDefinitionByPredicate,
        isSystemStyle,
        registerComponentDefinitionAndSchemas,
        registerDataTypeSchema,
        getDefinition,
        getSchema,
        isDraftDataSchema: core.isDraftDataSchema,
        isDraftItem: core.isDraftItem,
        extractReferences: core.extractReferences,
        extractReferenceFieldsInfo: core.extractReferenceFieldsInfo,
        extractOwnedReferenceFieldsInfo: core.extractOwnedReferenceFieldsInfo,
        addReferences: core.addReferences,
        hasNamespace: core.hasNamespace,
        hasSchemaForDataType: core.hasSchemaForDataType,
        isPermanentDataType: core.isPermanentDataType,
        removeAdditionalProperties,
        validate,
        // @ts-ignore
        addDefaultsAndValidate: validate,
        validateStrict,
        validateNoDefaults,
        createItemAccordingToSchema,
        isValid, // TODO: Adjust API to include errors like in old service
        isContainer,
        isPage,
        isRepeater,
        isRefComponent,
        getComponentType,
        getNamespaceConfig,
        whitelistCleanup,
        addDataTypesToExistingNamespace: core.addDataTypesToExistingNamespace,
        options: core.options ?? {}
    }
}

const getInstanceName = (experimentInstance?: {isOpen(name: string): boolean}, config?: Partial<DSConfig>): string => {
    if (!config?.enforceStrictValidationEverywhere && experimentInstance?.isOpen('dm_useValidateTwiceAndReport'))
        return 'dm_useValidateTwiceAndReport'
    if (config?.enforceStrictValidationEverywhere && experimentInstance?.isOpen('dm_useValidateTwiceAndReport'))
        return 'dm_useValidateTwiceAndReport_and_enforceStrictValidationEverywhere'
    if (config?.enforceStrictValidationEverywhere) {
        return 'dm_enforceStrictValidationEverywhere'
    }
    return 'default'
}

const getExperimentalOptions = (instanceName: string): Record<string, boolean> => {
    const experimentalOptions = {
        dm_useValidateTwiceAndReport: {
            validateTwiceAndReport: true,
            useDefaultStrict: false
        },
        dm_enforceStrictValidationEverywhere: {
            validateTwiceAndReport: false,
            useDefaultStrict: true
        },
        dm_useValidateTwiceAndReport_and_enforceStrictValidationEverywhere: {
            validateTwiceAndReport: true,
            useDefaultStrict: true
        },
        default: {
            validateTwiceAndReport: false,
            useDefaultStrict: false
        }
    }
    if (!experimentalOptions[instanceName]) {
        throw new Error(`Instance name: ${instanceName} has no experimental options`)
    }
    return experimentalOptions[instanceName]
}

// The purpose of this closure is to hold different instances of the schema-service based on experiments.
// We need it because DMS is running only one instance of this service. Therefore, if we
// want to have experiments, we need multiple instances to be available at run time.
const experimentalInstances: Record<string, SchemaService> = {}

const getInstanceByExperiments = (() => {
    return (
        experimentInstance?: {isOpen(name: string): boolean},
        config?: Partial<DSConfig>,
        logger?: BaseLogger
    ): SchemaService => {
        const instanceName = getInstanceName(experimentInstance, config)

        if (!experimentalInstances[instanceName]) {
            experimentalInstances[instanceName] = createService(
                getUnifiedSchemasConfig(),
                getExperimentalOptions(instanceName),
                logger
            )
        }

        return experimentalInstances[instanceName]
    }
})()

export {
    SchemaValidationErrorDetails,
    SchemaValidationError,
    createSchemaCore,
    createService,
    getInstanceByExperiments,
    getInstanceName,
    common,
    getExperimentalOptions
}
